import React, { type MutableRefObject, type ReactElement } from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Inline } from '@atlaskit/primitives/compiled/inline';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Stack } from '@atlaskit/primitives/compiled/stack';

import { PressableColorTile, type PressableColorTileProps } from './pressable-tile';

export const CURATED_TILES_PER_ROW = 10;

export type CuratedPaletteProps = Pick<
	PressableColorTileProps,
	'builtInColorMap' | 'onChange' | 'value' | 'analyticsSource'
> & {
	customColorRef: MutableRefObject<string | undefined>;
};

function getColorsPerRowFromPalette(colors: ReactElement[]): ReactElement[][] {
	return colors.reduce((resultArray: ReactElement[][], item: ReactElement, index: number) => {
		const chunkIndex = Math.floor(index / CURATED_TILES_PER_ROW);

		resultArray[chunkIndex] = resultArray[chunkIndex] || []; // start a new chunk
		resultArray[chunkIndex].push(item);

		return resultArray;
	}, []);
}

export const CuratedPalette = ({
	analyticsSource,
	builtInColorMap,
	customColorRef,
	onChange,
	value,
}: CuratedPaletteProps): React.JSX.Element => {
	const { knownColors } = builtInColorMap;

	// lowercasing value here since all knownColors are lowercased to support legacy colors
	value = value.toLowerCase();

	if (!knownColors.includes(value)) {
		customColorRef.current = value;
	}

	const customColor = customColorRef.current;
	let colors = knownColors;

	if (customColor && !colors.includes(customColor)) {
		if (colors.length >= CURATED_TILES_PER_ROW) {
			colors = [...knownColors, customColor];
		} else {
			colors = [customColor, ...knownColors];
		}
	}

	const colorTiles = colors.map((color: string) => (
		<PressableColorTile
			analyticsSource={analyticsSource}
			builtInColorMap={builtInColorMap}
			isSelected={color === value}
			key={color}
			onChange={onChange}
			value={color}
		/>
	));

	const colorTilesMap = getColorsPerRowFromPalette(colorTiles);

	return (
		<Stack space="space.075">
			{colorTilesMap.map((rowColors, index) => (
				<Inline
					space="space.075"
					key={`curated-palette-row-${index}-key`}
					testId={`curated-palette-row-${index}`}
				>
					{rowColors}
				</Inline>
			))}
		</Stack>
	);
};
